import React from 'react'
import {
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Users,
  Car,
  Eye,
  CheckCircle2,
  Clock3,
  Archive,
  Trash2,
} from 'lucide-react'
import type { EnquiryRecord, EnquiryStatus } from '../../types'
import { parseEnquiry } from './parseEnquiry'
import { formatBookingDate, formatBookingTime } from '../../utils/format'

interface EnquiryCardProps {
  enquiry: EnquiryRecord
  onSelect: (enquiry: EnquiryRecord) => void
  onStatusChange: (id: string, status: EnquiryStatus) => void
  onDelete: (id: string) => void
}

export const EnquiryCard: React.FC<EnquiryCardProps> = ({
  enquiry,
  onSelect,
  onStatusChange,
  onDelete,
}) => {
  const details = parseEnquiry(enquiry)

  const emailSubject = encodeURIComponent(`Re: ${enquiry.subject || 'Tour Enquiry'}`)
  const mailtoUrl = `mailto:${details.customerEmail}?subject=${emailSubject}`

  const initials = details.customerName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('') || 'C'

  return (
    <div className="flex flex-col justify-between rounded-2xl border border-gray-200/90 bg-white p-5 shadow-sm hover:shadow-md transition-all duration-200 hover:border-emerald-200 group">
      <div>
        {/* Top Header: Customer Info & Status Badge */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-10 rounded-full bg-emerald-100/80 text-emerald-800 font-bold text-sm flex items-center justify-center shrink-0 border border-emerald-200">
              {initials}
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-gray-900 text-base leading-tight truncate">
                {details.customerName}
              </h4>
              <p className="text-xs text-gray-400 mt-0.5">
                {formatBookingDate(enquiry.submittedAt)} · {formatBookingTime(enquiry.submittedAt)}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold shrink-0 ${
              enquiry.status === 'open'
                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                : enquiry.status === 'replied'
                ? 'bg-blue-100 text-blue-800 border border-blue-200'
                : 'bg-gray-100 text-gray-700 border border-gray-200'
            }`}
          >
            {enquiry.status === 'open' && <Clock3 className="size-3" />}
            {enquiry.status === 'replied' && <CheckCircle2 className="size-3" />}
            {enquiry.status === 'closed' && <Archive className="size-3" />}
            <span className="capitalize">{enquiry.status}</span>
          </span>
        </div>

        {/* Tour Title & Specifications Banner */}
        <div className="mt-4 rounded-xl bg-gray-50/80 border border-gray-100 p-3.5 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200/50">
              Private Tour
            </span>
            {details.estimatedPrice && (
              <span className="text-sm font-bold text-emerald-700">
                {details.estimatedPrice}
              </span>
            )}
          </div>

          <p className="font-semibold text-sm text-gray-900 leading-snug line-clamp-2">
            {details.tourTitle}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 gap-x-3 text-xs text-gray-600 pt-1 border-t border-gray-200/40">
            {details.departureDate && (
              <div className="flex items-center gap-1.5 truncate">
                <Calendar className="size-3.5 text-gray-400 shrink-0" />
                <span className="truncate">
                  {details.departureDate} {details.departureTime ? `@ ${details.departureTime}` : ''}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1.5 truncate">
              <Users className="size-3.5 text-gray-400 shrink-0" />
              <span>
                {details.adults} Adults{details.children > 0 ? `, ${details.children} Kids` : ''}
              </span>
            </div>

            {details.pickupLocation && (
              <div className="flex items-center gap-1.5 truncate col-span-1 sm:col-span-2">
                <MapPin className="size-3.5 text-gray-400 shrink-0" />
                <span className="truncate">{details.pickupLocation}</span>
              </div>
            )}

            {details.vehicle && (
              <div className="flex items-center gap-1.5 truncate col-span-1 sm:col-span-2">
                <Car className="size-3.5 text-gray-400 shrink-0" />
                <span className="truncate">{details.vehicle}</span>
              </div>
            )}
          </div>

          {details.notes && (
            <p className="text-xs text-amber-900 bg-amber-50/60 rounded px-2.5 py-1.5 border border-amber-200/60 line-clamp-2">
              <span className="font-semibold">Note:</span> {details.notes}
            </p>
          )}
        </div>

        {/* Contact info links */}
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          {details.customerEmail && (
            <a
              href={mailtoUrl}
              className="inline-flex items-center gap-1 hover:text-emerald-700 truncate"
              title="Click to email"
            >
              <Mail className="size-3.5 text-gray-400" />
              <span className="truncate">{details.customerEmail}</span>
            </a>
          )}
          {details.customerPhone && (
            <a
              href={`tel:${details.customerPhone}`}
              className="inline-flex items-center gap-1 hover:text-emerald-700 truncate"
              title="Click to call"
            >
              <Phone className="size-3.5 text-gray-400" />
              <span>{details.customerPhone}</span>
            </a>
          )}
        </div>
      </div>

      {/* Action Footer */}
      <div className="mt-5 pt-3.5 border-t border-gray-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <select
            value={enquiry.status}
            onChange={(e) => onStatusChange(enquiry.id, e.target.value as EnquiryStatus)}
            className="text-xs rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-emerald-500 cursor-pointer"
          >
            <option value="open">Open</option>
            <option value="replied">Replied</option>
            <option value="closed">Closed</option>
          </select>

          <button
            onClick={() => {
              if (confirm('Delete this enquiry?')) {
                onDelete(enquiry.id)
              }
            }}
            className="p-1 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
            title="Delete enquiry"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={mailtoUrl}
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-200/60 hover:bg-emerald-100/60 transition-colors"
          >
            <Mail className="size-3" />
            <span>Reply</span>
          </a>

          <button
            onClick={() => onSelect(enquiry)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3 py-1 rounded-lg shadow-sm transition-colors"
          >
            <Eye className="size-3" />
            <span>Details</span>
          </button>
        </div>
      </div>
    </div>
  )
}
